import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileText, Play, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Asterisk } from "@/components/Brand";
import CaseWatch from "@/components/CaseWatch";
import NavigationHeader from "@/components/NavigationHeader";
import CommentSection from "@/components/CommentSection";
import { LOWDOWN_POSTS, liveLowdownPosts } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";
import { scrollTo } from "@/lib/motion";
import StoryShare from "@/components/StoryShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LowdownPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: posts } = useQuery({
    queryKey: ["lowdown"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/lowdown`);
        return data?.length ? liveLowdownPosts(data) : liveLowdownPosts(LOWDOWN_POSTS);
      } catch {
        return liveLowdownPosts(LOWDOWN_POSTS);
      }
    },
    initialData: liveLowdownPosts(LOWDOWN_POSTS),
    initialDataUpdatedAt: 0,
  });

  // The curated document links live in the bundled mirror too — if the API is
  // stale (older backend build on prod), the mirror's docs still render
  // instead of vanishing.
  const stripEmptyDocs = (p) => {
    if (!p) return p;
    const { docs, docs_url, docs_label, ...rest } = p;
    if (docs && docs.length) rest.docs = docs;
    if (docs_url) rest.docs_url = docs_url;
    if (docs_label) rest.docs_label = docs_label;
    return rest;
  };

  const post = (() => {
    const live = posts.find((p) => p.slug === slug);
    if (!live) return undefined;
    const mirror = LOWDOWN_POSTS.find((p) => p.slug === slug);
    return { ...stripEmptyDocs(mirror), ...stripEmptyDocs(live) };
  })();

  // Body text supports inline links: [label](url) → real, visible anchor.
  const renderWithLinks = (text) =>
    text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
      const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (!m) return part;
      return (
        <a
          key={i}
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`body-link-${i}`}
          className="font-semibold text-cherry underline decoration-cherry/60 underline-offset-4 transition-colors hover:text-pink"
        >
          {m[1]}
        </a>
      );
    });

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink px-6 text-center">
        <Asterisk className="h-8 w-8 text-cherry" />
        <p className="font-display text-2xl font-bold">That take decoded itself away.</p>
        <button
          data-testid="lowdown-post-back-notfound"
          onClick={() => navigate("/lowdown")}
          className="rounded-full bg-cherry px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-transform hover:scale-105"
        >
          Back to The Lowdown
        </button>
      </div>
    );
  }

  const goHome = () => {
    navigate("/");
    setTimeout(() => scrollTo("#platforms"), 400);
  };

  const sharePost = async () => {
    const url = `${window.location.origin}/api/share/${slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it in your DMs, posts, anywhere.");
    } catch {
      toast.error("Couldn't copy automatically — copy the address bar link.");
    }
  };

  return (
    <div className="relative min-h-screen bg-ink font-body text-white">
      <NavigationHeader />
      <article className="mx-auto max-w-3xl px-4 pb-24 pt-36 sm:px-6 lg:pt-44">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.button
            variants={fadeUp}
            data-testid="lowdown-post-back"
            onClick={() => navigate("/lowdown")}
            className="mb-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-cherry"
          >
            <ArrowLeft className="h-4 w-4" /> All the lowdown
          </motion.button>

          <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-cherry/35 bg-cherry/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-cherry">
                {post.tag === "Developing" && (
                  <span data-testid="developing-dot" className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cherry align-middle" />
                )}
                {post.tag}
              </span>
              <span className="text-[11px] font-medium text-white/45">{post.date}</span>
              {post.updated && (
                <span className="text-[11px] font-medium tracking-wide text-cherry/90">· UPDATED&nbsp;{post.updated}</span>
              )}
            </span>
            <StoryShare path={`/lowdown/${post.slug}/story-card`} title={post.title} />
          </motion.div>

          <motion.h1
            variants={fadeUp}
            data-testid="lowdown-post-title"
            className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
          >
            {post.title}
          </motion.h1>

          {post.content_note && (
            <motion.p
              variants={fadeUp}
              className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-xs leading-relaxed text-white/50"
            >
              {post.content_note}
            </motion.p>
          )}

          {post.image_url && (
            <motion.figure variants={fadeUp} className="mt-9">
              <img
                src={post.image_url}
                alt={post.title}
                className="max-h-[640px] w-full rounded-3xl border border-white/10 object-contain bg-panel/60"
              />
              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
                {post.image_caption || "On the scene."}
              </figcaption>
            </motion.figure>
          )}

          <motion.div
            variants={fadeUp}
            className="mt-10 space-y-6 border-l-2 border-cherry/40 pl-5 sm:pl-7"
          >
            {post.body.split("\n\n").map((para, i) =>
              para.startsWith("> ") ? (
                <blockquote
                  key={i}
                  data-testid={`lowdown-pull-quote-${i}`}
                  className="whitespace-pre-line font-garamond text-2xl italic leading-snug text-cherry sm:text-3xl"
                >
                  {para.slice(2)}
                </blockquote>
              ) : (
                <p key={i} className="text-base leading-relaxed text-white/75 sm:text-lg">
                  {renderWithLinks(para)}
                </p>
              )
            )}
          </motion.div>

          {post.docs && post.docs.length > 0 && (
            <motion.aside
              variants={fadeUp}
              data-testid="story-documents-panel"
              className="mt-10 rounded-3xl border border-cherry/50 bg-panel/70 p-6 sm:p-8"
            >
              <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-cherry">
                <FileText className="h-4 w-4" />
                The actual documents — crowned by the desk
              </p>
              <ul className="mt-5 space-y-2">
                {post.docs.map((doc, di) => (
                  <li key={doc.url}>
                    <a
                      data-testid={`story-doc-link-${di}`}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-ink/60 px-5 py-4 transition-colors hover:border-cherry/60 hover:bg-cherry/10"
                    >
                      <span className="text-sm font-semibold text-white/85 group-hover:text-white sm:text-base">
                        {doc.label}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-cherry">
                        Read (PDF)
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-white/40">
                Public court records — every page watermarked with the CGD crown.
              </p>
            </motion.aside>
          )}

          {post.image_url_2 && (
            <motion.figure variants={fadeUp} className="mt-10">
              <img
                src={post.image_url_2}
                alt={post.image_caption_2 || "From the story"}
                className="max-h-[560px] w-full rounded-3xl border border-white/10 object-contain bg-panel/60"
              />
              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
                {post.image_caption_2 || "On the scene."}
              </figcaption>
            </motion.figure>
          )}

          {post.image_url_3 && (
            <motion.figure variants={fadeUp} className="mt-10">
              <img
                src={post.image_url_3}
                alt={post.image_caption_3 || "From the story"}
                className="max-h-[560px] w-full rounded-3xl border border-white/10 object-contain bg-panel/60"
              />
              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
                {post.image_caption_3 || "On the scene."}
              </figcaption>
            </motion.figure>
          )}

          {post.video_url && (
            <motion.figure variants={fadeUp} className="mt-10">
              <video
                data-testid="lowdown-post-video"
                src={post.video_url}
                controls
                preload="none"
                playsInline
                className="w-full rounded-3xl border border-white/10"
              />
              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
                {post.video_caption || "Straight from the barricade."}
              </figcaption>
            </motion.figure>
          )}

          {post.video_url_2 && (
            <motion.figure variants={fadeUp} className="mt-10">
              <video
                data-testid="lowdown-post-video-2"
                src={post.video_url_2}
                controls
                preload="none"
                playsInline
                className="w-full rounded-3xl border border-white/10"
              />
              <figcaption className="mt-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
                {post.video_caption_2 || "Straight from the barricade."}
              </figcaption>
            </motion.figure>
          )}

          {post.case_watch?.length > 0 && <CaseWatch entries={post.case_watch} />}

          <motion.div
            variants={fadeUp}
            data-testid="lowdown-post-verdict"
            className="mt-12 rounded-3xl border border-cherry/30 bg-panel p-7 sm:p-9"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
              Morgan's verdict
            </p>
            <p className="mt-3 whitespace-pre-line font-garamond text-2xl italic leading-snug text-white/90 sm:text-3xl">
              "{post.verdict}"
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-4">
            <button
              data-testid="lowdown-post-back-all"
              onClick={() => navigate("/lowdown")}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-cherry hover:text-cherry"
            >
              <ArrowLeft className="h-4 w-4" /> More lowdown
            </button>
            <button
              data-testid="lowdown-post-share-btn"
              onClick={sharePost}
              className="inline-flex items-center gap-2 rounded-full border border-cherry/50 bg-cherry/10 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cherry transition-all duration-300 hover:bg-cherry hover:text-white"
            >
              <Share2 className="h-4 w-4" /> Share this take
            </button>
            <button
              data-testid="lowdown-post-listen"
              onClick={goHome}
              className="inline-flex items-center gap-2 rounded-full bg-cherry px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-cherry-soft"
            >
              <Play className="h-4 w-4 fill-current" /> Listen to the show
            </button>
          </motion.div>

          <motion.div variants={fadeUp}>
            <CommentSection
              articleType="lowdown"
              slug={post.slug}
              accent="#FF0055"
              onAccent="#FFFFFF"
            />
          </motion.div>
        </motion.div>
      </article>

      <footer className="border-t border-white/10 px-4 py-10 text-center sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/35">
          © {new Date().getFullYear()} City Girls Decoded™. Raw. Unfiltered. Necessary.
        </p>
      </footer>
    </div>
  );
}
